import {
  ObjectFromEnum,
  convertEnumToArray,
  getEnumValueContainedInString,
} from '../../utils/functions/data.functions';
import { useEffect, useState } from 'react';
import { ColorPicker } from '../../components/colorPicker/ColorPicker';
import {
  Cell,
  FontChangeOption,
  FontSizeOption,
  ObjectAlignOptions,
  StructureOption,
  StyleType,
  Target,
} from './SmartAttachmentSpreadsheet.types';
import ButtonMenu, { BtnOption } from '../../components/buttonMenu/ButtonMenu';
import { Menu } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faBold,
  faFillDrip,
  faFont,
  faTableColumns,
  faUnderline,
} from '@fortawesome/free-solid-svg-icons';
import { Point } from 'react-spreadsheet';

interface Props {
  data: any;
  changeColClass: (data: any, styleType?: StyleType) => void;
  changeStructure: (data: any) => void;
  baseClassName: string;
  setBaseClassName: React.Dispatch<React.SetStateAction<string>>;
  selection?: Point[];
}

/**
 * Toolbar component for SmartAttachmentSpreadsheet
 */
export const SpreadsheetToolbar = ({
  data,
  changeColClass,
  changeStructure,
  baseClassName,
  setBaseClassName,
  selection,
}: Props) => {
  const [isSelectionBold, setIsSelectionBold] = useState<boolean>();
  const [isSelectionUnderlined, setIsSelectionUnderlined] = useState<boolean>();
  const [selectedTarget, setSelectedTarget] = useState<Target>(Target.BACKGROUND);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const [, /* selectedFontDecor */ setSelectedFontDecor] = useState<FontChangeOption | string>('');
  const [selectedFontSize, setSelectedFontSize] = useState<FontSizeOption | number>(12);
  const [/* selectedObjectAlign */, setSelectedObjectAlign] = useState<ObjectAlignOptions | string>(
    getEnumValueContainedInString(baseClassName, ObjectAlignOptions),
  );
  /**
   * Sets target and opens color picker
   * @param target
   */
  const handleClickColorPicker = (target: Target) => {
    setSelectedTarget(target);
    setShowColorPicker(true);
  };

  /**
   *  Function to add a new row at the end
   */
  const handleAddRow = () => {
    const newData = [...data, ['', '']];
    changeStructure(newData);
  };

  /**
   * Function to add a new column at the end
   */
  const handleAddColumn = () => {
    const newData = data.map((row: any) => [...row, '']);
    changeStructure(newData);
  };
  /**
   *  Function to add a new row at the end
   */
  const handleRemoveRow = () => {
    const newData = data.filter((_: never, index: number) => index !== data.length - 1);
    changeStructure(newData);
  };

  /**
   * Function to add a new column at the end
   */
  const handleRemoveColumn = () => {
    const newData = data.map((row: any) => row.slice(0, -1));
    changeStructure(newData);
  };

  /**
   * Handle change structure action
   * @param option
   */
  const handleChangeStructure = (option: StructureOption) => {
    switch (option) {
      case StructureOption.ADD_ROW:
        handleAddRow();
        break;
      case StructureOption.ADD_COLUMN:
        handleAddColumn();
        break;
      case StructureOption.REMOVE_ROW:
        handleRemoveRow();
        break;
      case StructureOption.REMOVE_COLUMN:
        handleRemoveColumn();
        break;
      default:
        break;
    }
  };

  /**
   * Change font size
   * @param option
   */
  const handleChangeFontSize = (option: FontSizeOption) => {
    const styleType: StyleType = { before: 'text-', after: undefined };
    switch (option) {
      case FontSizeOption.PX_12:
        changeColClass(`text-xs`, styleType);
        break;
      case FontSizeOption.PX_14:
        changeColClass(`text-sm`, styleType);
        break;
      case FontSizeOption.PX_16:
        changeColClass(`text-base`, styleType);
        break;
      case FontSizeOption.PX_18:
        changeColClass(`text-lg`, styleType);
        break;
      case FontSizeOption.PX_24:
        changeColClass(`text-2xl`, styleType);
        break;
      default:
        break;
    }
    setSelectedFontSize(option);
  };

  /**
   * Change text decoration
   * @param option
   */
  const handleChangeColClass = (option: FontChangeOption) => {
    switch (option) {
      case FontChangeOption.BOLD:
        changeColClass(`font-bold`);
        break;
      case FontChangeOption.UNDERLINE:
        changeColClass(`underline`);
        break;
      default:
        break;
    }
    setSelectedFontDecor('');
  };

  /**
   * Sets float position for spreadsheet
   * @param option
   */
  const handleChangeObjectAlign = (option: ObjectAlignOptions) => {
    switch (option) {
      case ObjectAlignOptions.LEFT:
        setBaseClassName(`float-left`);
        break;
      case ObjectAlignOptions.RIGHT:
        setBaseClassName(`float-right`);
        break;
      case ObjectAlignOptions.CENTER:
        setBaseClassName(`ml-auto mr-auto`);
        break;
      default:
        break;
    }
    setSelectedObjectAlign(option);
  };

  /**
   * Change column target color
   * @param color - color hex value
   * @param target - bg / text
   */
  const handleChangeColor = (color: string, target: Target) => {
    const _color = `${target}-${color}`;
    changeColClass(_color, { before: target + '-', after: '-500' });
    setShowColorPicker(false);
  };

  /**
   * Prepares font sizes for button menu
   */
  const getFontSizeOptions = () => {
    let options: BtnOption[] = [];
    convertEnumToArray(FontSizeOption).map((opt: ObjectFromEnum) => {
      options = [
        ...options,
        {
          title: opt.name,
          onClick: () => handleChangeFontSize(opt.value as FontSizeOption),
        },
      ];
    });
    return options;
  };

  /**
   * Function that returns selected cells
   */
  const getCellsInSelection = () => {
    if (!selection || !data) return;
    const result = [];
    for (let i = 0; i < selection.length; i++) {
      const { row, column } = selection[i];
      if (data[row] && data[row][column]) {
        result.push(data[row][column]);
      }
    }
    return result as Cell[];
  };

  /**
   * Return true if all selected cells has style
   * @param style
   */
  const checkIfAllSelectedHasStyle = (style: string) => {
    let contains = true;
    const cells = getCellsInSelection();
    if (!cells) return;
    cells.map((c: Cell) => {
      if (!c.className.includes(style)) contains = false;
    });
    return contains;
  };

  useEffect(() => {
    setIsSelectionBold(checkIfAllSelectedHasStyle(FontChangeOption.BOLD));
    setIsSelectionUnderlined(checkIfAllSelectedHasStyle(FontChangeOption.UNDERLINE));
  }, [selection]);

  const baseButtonStyle = `mx-1 px-1.5 border border-hidden py-1.5 cursor-pointer text-gray-600`;
  const selected = `bg-gray-200 border border-gray-300 rouded-lg`;
  return (
    <div className="flex absolute -top-12 px-3 items-center border border-[#ccc] border-r-0 border-l-0 w-full z-50">
      {/* Font decoration */}
      <FontAwesomeIcon
        icon={faBold}
        onClick={() => handleChangeColClass(FontChangeOption.BOLD)}
        className={`${baseButtonStyle} ${isSelectionBold && selected}`}
      />
      <FontAwesomeIcon
        icon={faUnderline}
        onClick={() => handleChangeColClass(FontChangeOption.UNDERLINE)}
        className={`${baseButtonStyle} ${isSelectionUnderlined && selected}`}
      />

      {/* Change structure */}
      <ButtonMenu
        icon={
          <Menu.Button type="button" className={baseButtonStyle}>
            <FontAwesomeIcon icon={faTableColumns} />
          </Menu.Button>
        }
        options={[
          {
            title: 'add-column',
            onClick: () => handleChangeStructure(StructureOption.ADD_COLUMN),
          },
          {
            title: 'add-row',
            onClick: () => handleChangeStructure(StructureOption.ADD_ROW),
          },
          {
            title: 'remove-column',
            onClick: () => handleChangeStructure(StructureOption.REMOVE_COLUMN),
          },
          {
            title: 'remove-row',
            onClick: () => handleChangeStructure(StructureOption.REMOVE_ROW),
          },
        ]}
      />
      {/* Change base obj align */}
      <ButtonMenu
        icon={
          <Menu.Button type="button" className={baseButtonStyle}>
            <FontAwesomeIcon icon={faAlignCenter} />
          </Menu.Button>
        }
        options={[
          {
            title: <FontAwesomeIcon icon={faAlignCenter} />,
            className: `${
              baseClassName.includes(ObjectAlignOptions.CENTER) ? `text-gray-900` : `text-gray-500`
            }`,
            onClick: () => handleChangeObjectAlign(ObjectAlignOptions.CENTER),
          },
          {
            title: <FontAwesomeIcon icon={faAlignLeft} />,
            className: `${
              baseClassName.includes(ObjectAlignOptions.LEFT) ? `text-gray-900` : `text-gray-500`
            }`,
            onClick: () => handleChangeObjectAlign(ObjectAlignOptions.LEFT),
          },
          {
            title: <FontAwesomeIcon icon={faAlignRight} />,
            className: `${
              baseClassName.includes(ObjectAlignOptions.RIGHT) ? `text-gray-900` : `text-gray-500`
            }`,
            onClick: () => handleChangeObjectAlign(ObjectAlignOptions.RIGHT),
          },
        ]}
      />
      {/* Change font size */}
      <ButtonMenu
        icon={
          <Menu.Button type="button" className={baseButtonStyle}>
            <span>{selectedFontSize}</span>
          </Menu.Button>
        }
        options={getFontSizeOptions()}
      />
      {/* Background color */}
      <div className="relative inline px-3 text-gray-600">
        <FontAwesomeIcon
          icon={faFillDrip}
          className={`underline`}
          onClick={() => handleClickColorPicker(Target.BACKGROUND)}
        />
      </div>
      {/* Text color */}
      <div className="relative inline px-3 text-gray-600">
        <FontAwesomeIcon icon={faFont} onClick={() => handleClickColorPicker(Target.TEXT)} />
      </div>
      {/* Color picker */}
      {showColorPicker && (
        <div className="absolute z-30 right-[-20px]">
          <div className="absolute flex top-5">
            <ColorPicker onChange={(color: string) => handleChangeColor(color, selectedTarget)} />
          </div>
        </div>
      )}
    </div>
  );
};
